/**
 * PaymentMethods component for editing payment methods.
 *
 * This component displays the user's saved payment methods, allows selection,
 * and provides options to add or remove payment methods. It fetches payment options
 * from the backend, manages the selected payment method state, and displays details
 * of the currently selected payment method. Integrates with a card carousel for
 * visual selection and uses modals for adding/removing cards.  NOT FOR MAKING PAYMENT
 *
 * @component
 */
import { motion } from 'framer-motion';
import { PaymentOption } from '../../../helpers/utils/types';
import { Pencil, AlertCircle, CreditCard, ArrowRight, ArrowLeft, RefreshCw } from 'lucide-react';
import { useModal } from '../../../helpers/hooks/modalContext';
import { useAuth } from '../../../auth/Auth';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  updatePaymentOption,
  verifyPaymentOption,
} from '../../../auth/_requests';
import { showToast } from '../../../helpers/utils/showToast';
import CardCarousal from '../CardCarousal/CardCarousal';
import { Icon } from '../../../helpers/Icons/Icon';
import { FetchAddress } from '../../../helpers/utils/utils';
import { useFormik } from 'formik';
import NoPaymentOptions from '../NoPaymentOptions/NoPaymentOptions';
import { Spinner } from '../../../helpers/UI/Spinner/Spinner';
import { usePaymentOptions } from '../../../helpers/hooks/PaymentOptionsContext';
import { ButtonSpinner } from '../../../helpers/UI/ButtonSpinner/ButtonSpinner';
import {
  TenantPaymentOptionStatusEnum,
  getPaymentOptionStatusLabel,
  getPaymentOptionStatusMessage,
} from '../../../helpers/utils/types';
import { Card } from '../../PrizeDraw/components/ui';
import { useDeepLinkFulfillment } from '../../../helpers/hooks/useDeepLinkFulfillment';
import { DeepLinkIntent } from '../../../helpers/utils/deepLink';

const itemVariant = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] as const },
  },
};

const PaymentMethods: React.FC = () => {
  const { setModal } = useModal();
  const { paymentOptions, loading, refreshPaymentOptions } = usePaymentOptions();
  const { auth } = useAuth() as { auth: { data: { token: string } } };

  const [selectedPaymentOption, setSelectedPaymentOption] = useState<PaymentOption | null>(null);
  const [, setSelectedMethod] = useState<string | null>(
    localStorage.getItem('selectedPaymentOption')
  );
  const addressInputRef = useRef(null);
  const carouselScrollRef = useRef<HTMLDivElement>(null);
  const [isSaveManually, setIsManually] = useState(false);
  const [, setIsAutocompleteOpen] = useState(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [verificationAmount, setVerificationAmount] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [verificationAmountError, setVerificationAmountError] = useState<string>('');

  const handleVerificationAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setVerificationAmountError('');

    if (val === '') {
      setVerificationAmount(val);
      return;
    }

    // Allow only positive decimals (e.g. "1", "1.5", "0.99") — rejects letters and symbols
    if (!/^\d*\.?\d*$/.test(val)) return;

    const numVal = parseFloat(val);

    // Allow in-progress decimal input like ".", "0.", or "0.0x" before the value is complete
    const isPartialDecimal =
      val === '.' || val.endsWith('.') || val === '0' || val.startsWith('0.');

    if (isPartialDecimal || (!isNaN(numVal) && numVal <= 2.0)) {
      setVerificationAmount(val);
    }
  };

  const isPreAuthorizedDebit =
    selectedPaymentOption?.paymentMethod?.toLowerCase() === 'preauthorizeddebit';
  const isVerificationPending =
    selectedPaymentOption?.status === TenantPaymentOptionStatusEnum.VerificationPending;

  useEffect(() => {
    const paymentOptionId = localStorage.getItem('selectedPaymentOption');

    if (paymentOptionId) {
      const foundOption = paymentOptions.find((option) => option.id === paymentOptionId);
      if (foundOption) {
        setSelectedPaymentOption(foundOption);
        formik.setValues({
          address: foundOption?.address || '',
          unitNumber: foundOption?.unitNumber || '',
          street: '',
          house: '',
          postal: foundOption?.postalCode || '',
          city: foundOption?.city || '',
          province: '',
          provinceCode: foundOption?.provinceCode || '',
          country: foundOption?.country || 'CA',
          isDefault: foundOption?.isDefault || false,
        });
      }
    }
  }, [paymentOptions]);

  useEffect(() => {
    const fetchPaymentOptions = async () => {
      if (!auth?.data.token) return;
      try {
        const options = paymentOptions;
        if (options.length > 0) {
          if (!selectedPaymentOption) {
            handleSelectMethod(options[0].id);
            setSelectedPaymentOption(options[0]);
          }
        }
        const paymentOptionId = localStorage.getItem('selectedPaymentOption');
        if (paymentOptionId) {
          const foundOption = options.find((option) => option.id === paymentOptionId);
          if (foundOption) {
            setSelectedPaymentOption(foundOption);
            setSelectedMethod(paymentOptionId);
          }
        }
      } catch (error) {
        showToast({
          message: 'Error fetching payment options: ' + error,
          type: 'error',
        });
      }
    };
    fetchPaymentOptions();
  }, [auth, paymentOptions]);

  const handleSelectMethod = async (id: string) => {
    setSelectedMethod(id);
    const selectedOption = paymentOptions?.find((option) => option.id === id);

    if (selectedOption) {
      localStorage.setItem('selectedPaymentOption', selectedOption.id);
      setSelectedPaymentOption({ ...selectedOption });
    }
    formik.setValues({
      address: selectedOption?.address || '',
      unitNumber: selectedOption?.unitNumber || '',
      street: '',
      house: '',
      postal: selectedOption?.postalCode || '',
      city: selectedOption?.city || '',
      province: '',
      provinceCode: selectedOption?.provinceCode || '',
      country: selectedOption?.country || 'CA',
      isDefault: !!selectedOption?.isDefault,
    });
  };

  const shiftPaymentCarousel = (dir: 1 | -1) => {
    carouselScrollRef.current?.scrollBy({ left: dir * 330, behavior: 'smooth' });
  };

  const handleRemove = () => {
    setModal('remove-card', { editCard: selectedPaymentOption as PaymentOption });
  };

  const formik = useFormik({
    initialValues: {
      address: '',
      unitNumber: '',
      street: '',
      house: '',
      postal: '',
      city: '',
      province: '',
      provinceCode: '',
      country: 'CA',
      isDefault: false,
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: any = {};
      const isPAD = selectedPaymentOption?.paymentMethod?.toLowerCase() === 'preauthorizeddebit';

      if (!isPAD) {
        if (!values.address) {
          errors.address = 'Address is required';
        }
        if (!values.postal) {
          errors.postal = 'Postal Code is required';
        } else if (values.postal.length < 2) {
          errors.postal = 'Postal Code must be at least 2 characters';
        }
        if (!values.city) {
          errors.city = 'City is required';
        }
      }

      if (!values.country) {
        errors.country = 'Country is required';
      }

      return errors;
    },
    onSubmit: async (values) => {
      try {
        setIsSaving(true);

        if (isPreAuthorizedDebit) {
          await updatePaymentOption(auth?.data?.token, selectedPaymentOption?.id || '', {
            isDefault: values.isDefault,
          } as any);
        } else {
          await updatePaymentOption(auth?.data?.token, selectedPaymentOption?.id || '', {
            billingInfo: {
              fullAddress: values.address,
              countryCode: 'CA',
              postalCode: values.postal,
              provinceCode: values.provinceCode || undefined,
              city: values.city,
            },
            isDefault: values.isDefault,
          });
        }
        await refreshPaymentOptions();
        showToast({
          message: 'Payment option updated successfully',
          type: 'success',
        });
      } catch (error) {
        showToast({
          message: 'Error saving address: ' + error,
          type: 'error',
        });
      } finally {
        setIsSaving(false);
        setIsManually(false);
      }
    },
  });

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      Object.keys(formik.errors).forEach((field) => {
        formik.setFieldTouched(field, true, false);
      });
      setIsManually(true);
    } else {
      setIsManually(false);
    }
  }, [formik.errors]);

  const handleVerifyPAD = async () => {
    if (!selectedPaymentOption?.id || !auth?.data.token) {
      showToast({
        message: 'Missing payment option or authentication',
        type: 'error',
      });
      return;
    }

    const amount = parseFloat(verificationAmount);
    if (isNaN(amount) || amount < 0.01 || amount > 2.0) {
      setVerificationAmountError('Please enter a valid amount between $0.01 and $2.00');
      return;
    }

    setVerificationAmountError('');

    try {
      setIsVerifying(true);
      await verifyPaymentOption(auth.data.token, selectedPaymentOption.id, amount);
      showToast({
        message: 'Verification initiated successfully',
        type: 'success',
      });
      await refreshPaymentOptions();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'An error occurred while verifying payment option';
      showToast({
        message: errorMessage,
        type: 'error',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const fulfillAddPaymentMethodDeepLink = useCallback(
    (intent: DeepLinkIntent) => {
      setModal('add-payment-method', {
        tenantBuildingId: intent.tenantBuildingId,
      });
    },
    [setModal]
  );

  useDeepLinkFulfillment('add_payment_method', fulfillAddPaymentMethodDeepLink, !loading);

  const openAddPaymentMethodModal = () => {
    setModal('add-payment-method', {
      tenantBuildingId: undefined,
    });
  };

  if (loading) {
    return <Spinner />;
  }

  const paymentsHero = (
    <div className='hero'>
      <div className='pg-hero rounded-[16px] sm:rounded-[20px]'>
        <div className='hero-beam' aria-hidden />
        <div className='hero-orb orb-blue' aria-hidden />
        <div className='orb-ice-p' aria-hidden />
        <div className='relative z-[1] px-4 py-5 sm:px-8 sm:py-7'>
          <div className='pg-hero-eye'>Account</div>
          <div className='pg-hero-title text-[20px] sm:text-[28px] md:text-[32px]'>Payment methods</div>
          <div className='pg-hero-sub text-[12px] sm:text-[13px]'>
            Add and manage the payment methods you use to pay rent through TenantPay.
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className='pg w-full text-main' id='pg-payments'>
      {paymentsHero}
      {paymentOptions && paymentOptions.length === 0 ? (
        <motion.div
          initial='hidden'
          animate='show'
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.12, delayChildren: 0.02 } },
          }}
        >
          <motion.div variants={itemVariant} className='card'>
            <NoPaymentOptions
              onAddPayment={() => openAddPaymentMethodModal()}
              isLoading={false}
            />
          </motion.div>
        </motion.div>
      ) : (
        <motion.section
          className='flex flex-col space-y-6'
          initial='hidden'
          animate='show'
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.12, delayChildren: 0.02 } },
          }}
        >
          <motion.div variants={itemVariant}>
            <Card className='p-4 sm:p-6 md:p-8 overflow-hidden'>
              <div className='flex flex-col gap-6 sm:gap-10' data-clarity-mask='true'>
                <div className='w-full flex flex-col gap-3 sm:gap-4'>
                  <div className='flex items-center justify-between gap-3'>
                    <div className='min-w-0 flex-1'>
                      <div className='text-[12px] sm:text-[13px] text-[#6B7285] leading-snug'>
                        Select a card or account. Use the arrows or swipe along the row to browse.
                      </div>
                    </div>
                    <div className='flex gap-1.5 sm:gap-2 flex-shrink-0'>
                      <button
                        type='button'
                        onClick={() => shiftPaymentCarousel(-1)}
                        className='w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-[#E8EAF0] bg-white flex items-center justify-center hover:bg-[#FAFBFD] transition-colors'
                        aria-label='Scroll payment methods left'
                      >
                        <ArrowLeft size={14} className='text-[#0A0B14] sm:w-4 sm:h-4' />
                      </button>
                      <button
                        type='button'
                        onClick={() => shiftPaymentCarousel(1)}
                        className='w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-[#E8EAF0] bg-white flex items-center justify-center hover:bg-[#FAFBFD] transition-colors'
                        aria-label='Scroll payment methods right'
                      >
                        <ArrowRight size={14} className='text-[#0A0B14] sm:w-4 sm:h-4' />
                      </button>
                    </div>
                  </div>

                  <CardCarousal
                    layout='scroll'
                    scrollRef={carouselScrollRef}
                    paymentOptions={paymentOptions}
                    selectedOptionId={selectedPaymentOption?.id ?? ''}
                    onSelectOption={handleSelectMethod}
                    onAddPaymentMethod={() =>
                      setModal('add-payment-method', {
                        tenantBuildingId: undefined,
                      })
                    }
                    editMode={true}
                    paymentType='direct'
                  />
                </div>

                <div className='w-full flex flex-col gap-6 border-t border-[#E9EDF5] pt-6 sm:pt-10'>
                  <FetchAddress
                    addressInputRef={addressInputRef}
                    formik={formik}
                    isSaveManually={setIsManually}
                    setIsAutocompleteOpen={setIsAutocompleteOpen}
                  />
                  <div className='rounded-[16px] border border-[#E8EAF0] bg-white p-4 sm:p-6 shadow-[0_1px_2px_rgba(10,11,20,0.04),0_4px_16px_rgba(10,11,20,0.06)]'>
                    <div className='flex flex-col gap-6 sm:gap-8'>
                      {/* Panel header */}
                      <div className='flex w-full justify-between items-center gap-2'>
                        <div className='min-w-0 flex-1'>
                          <div className='text-[10px] font-semibold uppercase tracking-[0.12em] text-[#6B7285] mb-0.5 sm:mb-1'>
                            Selected method
                          </div>
                          <span className='text-[15px] sm:text-[17px] font-semibold text-[#0A0B14] truncate block'>
                            {isPreAuthorizedDebit ? 'Bank account details' : 'Credit card details'}
                          </span>
                        </div>
                        <div
                          className='p-2 sm:p-2.5 flex justify-center items-center rounded-[12px] bg-[rgba(183,13,13,0.07)] cursor-pointer hover:bg-[rgba(183,13,13,0.12)] transition-colors flex-shrink-0'
                          onClick={handleRemove}
                        >
                          <Icon name='bin' className='text-[#B70D0D]' strokeWidth={0.2} />
                        </div>
                      </div>

                      <form onSubmit={formik.handleSubmit}>
                        <div className='w-full flex flex-col gap-6 sm:gap-8'>
                          {isVerificationPending ? (
                            <>
                              {/* Verification Section */}
                              <div className='flex flex-col gap-4 sm:gap-5 w-full'>
                                <div className='bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-[12px] p-4 sm:p-6 shadow-sm'>
                                  <div className='flex items-start gap-3 sm:gap-4'>
                                    <div className='flex-shrink-0'>
                                      <div className='w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center'>
                                        <AlertCircle
                                          className='w-5 h-5 sm:w-6 sm:h-6 text-blue-600'
                                          strokeWidth={2.5}
                                        />
                                      </div>
                                    </div>
                                    <div className='flex-1 min-w-0'>
                                      <h3 className='text-[14px] sm:text-[15px] font-semibold text-[#1E293B] mb-2'>
                                        Verify your payment method
                                      </h3>
                                      <p className='text-[12px] sm:text-[13px] text-[#475569] leading-[1.6] mb-4'>
                                        To complete your payment method setup, we need to verify
                                        ownership. Enter the exact{' '}
                                        <span className='font-semibold text-[#334155]'>
                                          micro-deposit
                                        </span>{' '}
                                        amount that appears on your statement (between $0.01 and
                                        $2.00).
                                      </p>
                                      <div className='flex items-start gap-2 mb-4 rounded-[8px] bg-emerald-50 border border-emerald-200 px-3 py-2.5'>
                                        <RefreshCw className='w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5' />
                                        <p className='text-[12px] sm:text-[13px] text-[#047857] leading-[1.5]'>
                                          The full micro-deposit will be automatically refunded
                                          within a few business days. No action is needed on your
                                          part.
                                        </p>
                                      </div>
                                      <div className='bg-white rounded-[8px] p-3.5 sm:p-4 border border-blue-200'>
                                        <div className='flex flex-col gap-3'>
                                          <div className='flex items-start gap-2.5 sm:gap-3'>
                                            <div className='flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] sm:text-xs font-semibold mt-0.5'>
                                              1
                                            </div>
                                            <div className='flex-1 min-w-0'>
                                              <p className='text-[12px] sm:text-[13px] font-medium text-[#334155] mb-0.5 sm:mb-1'>
                                                Check your statement
                                              </p>
                                              <p className='text-[11px] sm:text-[12px] text-[#64748B]'>
                                                Look for the micro-deposit from us on your statement
                                                (between $0.01 and $2.00)
                                              </p>
                                            </div>
                                          </div>
                                          <div className='flex items-start gap-2.5 sm:gap-3'>
                                            <div className='flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] sm:text-xs font-semibold mt-0.5'>
                                              2
                                            </div>
                                            <div className='flex-1 min-w-0'>
                                              <p className='text-[12px] sm:text-[13px] font-medium text-[#334155] mb-0.5 sm:mb-1'>
                                                Enter the micro-deposit amount
                                              </p>
                                              <p className='text-[11px] sm:text-[12px] text-[#64748B]'>
                                                Enter the exact micro-deposit amount shown on your
                                                statement below
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className='bg-white border-2 border-blue-200 rounded-[12px] p-4 sm:p-6 shadow-sm'>
                                  <div className='flex flex-col gap-4'>
                                    <div className='flex items-center gap-2 mb-2'>
                                      <CreditCard className='w-5 h-5 text-blue-600' />
                                      <label className='text-[15px] font-semibold text-[#1E293B]'>
                                        Enter verification amount
                                      </label>
                                    </div>
                                    <p className='text-[13px] text-[#64748B] mb-4'>
                                      Enter the exact micro-deposit amount (e.g., 0.25) that appears
                                      on your statement
                                    </p>
                                    <div>
                                      <label className='block text-[13px] font-medium text-[#475569] mb-2'>
                                        Verification amount <span className='text-red-500'>*</span>
                                      </label>
                                      <div className='flex flex-col sm:flex-row gap-4 items-start'>
                                        <div className='flex-1'>
                                          <div className='relative'>
                                            <span className='absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B] text-lg font-medium'>
                                              $
                                            </span>
                                            <input
                                              type='text'
                                              value={verificationAmount}
                                              onChange={handleVerificationAmountChange}
                                              placeholder='0.00'
                                              className='w-full pl-8 pr-4 py-3 border-2 border-[#E2E8F0] rounded-[8px] text-[#1E293B] text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all'
                                              onKeyDown={(e) => {
                                                if (
                                                  e.key === 'Enter' &&
                                                  verificationAmount &&
                                                  !isVerifying
                                                ) {
                                                  handleVerifyPAD();
                                                }
                                              }}
                                            />
                                          </div>
                                          {verificationAmountError ? (
                                            <p className='text-xs text-red-500 mt-2'>
                                              {verificationAmountError}
                                            </p>
                                          ) : (
                                            <p className='text-xs text-[#94A3B8] mt-2'>
                                              Amount must be between $0.01 and $2.00
                                            </p>
                                          )}
                                        </div>
                                        <div className='sm:w-auto w-full'>
                                          <button
                                            type='button'
                                            onClick={handleVerifyPAD}
                                            disabled={
                                              isVerifying ||
                                              !verificationAmount ||
                                              (() => {
                                                const amount = parseFloat(verificationAmount);
                                                return (
                                                  isNaN(amount) || amount < 0.01 || amount > 2.0
                                                );
                                              })()
                                            }
                                            className='submitBtn px-8 py-3 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed h-fit w-full sm:w-auto flex items-center justify-center gap-2 min-w-[140px]'
                                          >
                                            {isVerifying ? (
                                              <>
                                                <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                                                <span>Verifying...</span>
                                              </>
                                            ) : (
                                              <>
                                                <span>Verify account</span>
                                                <ArrowRight className='w-4 h-4' />
                                              </>
                                            )}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className='bg-amber-50 border border-amber-200 rounded-[8px] p-4'>
                                  <div className='flex items-start gap-3'>
                                    <AlertCircle className='w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5' />
                                    <div>
                                      <p className='text-sm text-[#92400E] font-medium mb-1'>
                                        Can't find the micro-deposit?
                                      </p>
                                      <p className='text-xs text-[#B45309]'>
                                        The micro-deposit typically appears within 1-3 business days.
                                        Check your recent transactions. If you still can't find it
                                        after 3 days, please contact support.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Collapsed bank/card details */}
                              <div className='border-t border-[#E8EAF0] pt-5'>
                                <details className='group'>
                                  <summary className='cursor-pointer list-none flex items-center justify-between text-[13px] font-medium text-[#64748B] hover:text-[#475569] transition-colors'>
                                    <span>
                                      {isPreAuthorizedDebit
                                        ? 'View bank account details'
                                        : 'View card details'}
                                    </span>
                                    <span className='text-xs'>▼</span>
                                  </summary>
                                  <div className='mt-4 flex flex-col gap-5'>
                                    {isPreAuthorizedDebit ? (
                                      <>
                                        <div className={'inputWrapper'}>
                                          <div className={'label-light'}>Bank name*</div>
                                          <div className={'no-border-input-field'}>
                                            <input
                                              type='text'
                                              value={selectedPaymentOption?.bankName || ''}
                                              disabled
                                              readOnly
                                              className='text-light/60'
                                            />
                                          </div>
                                        </div>
                                        <div className={'inputWrapper'}>
                                          <div className={'label-light'}>Account name*</div>
                                          <div className={'no-border-input-field'}>
                                            <input
                                              type='text'
                                              value={selectedPaymentOption?.bankAccountName || ''}
                                              disabled
                                              readOnly
                                              className='text-light/60'
                                            />
                                          </div>
                                        </div>
                                        <div className='flex flex-col sm:flex-row gap-5 w-full'>
                                          <div className={'inputWrapper w-full'}>
                                            <div className={'label-light'}>Transit number*</div>
                                            <div className={'no-border-input-field'}>
                                              <input
                                                type='text'
                                                value={
                                                  selectedPaymentOption?.bankTransitNumber || ''
                                                }
                                                disabled
                                                readOnly
                                                className='text-light/60'
                                              />
                                            </div>
                                          </div>
                                          <div className={'inputWrapper w-full'}>
                                            <div className={'label-light'}>Institution number*</div>
                                            <div className={'no-border-input-field'}>
                                              <input
                                                type='text'
                                                value={
                                                  selectedPaymentOption?.bankInstitutionNumber || ''
                                                }
                                                disabled
                                                readOnly
                                                className='text-light/60'
                                              />
                                            </div>
                                          </div>
                                        </div>
                                        <div className={'inputWrapper'}>
                                          <div className={'label-light'}>Account number*</div>
                                          <div className={'no-border-input-field'}>
                                            <input
                                              type='text'
                                              value={
                                                selectedPaymentOption?.bankAccountNumber
                                                  ? selectedPaymentOption.bankAccountNumber.length >
                                                    4
                                                    ? `****${selectedPaymentOption.bankAccountNumber.slice(-4)}`
                                                    : selectedPaymentOption.bankAccountNumber
                                                  : ''
                                              }
                                              disabled
                                              readOnly
                                              className='text-light/60'
                                            />
                                          </div>
                                        </div>
                                        {selectedPaymentOption?.status && (
                                          <div className={'inputWrapper'}>
                                            <div className={'label-light'}>Status*</div>
                                            <div className={'no-border-input-field'}>
                                              <input
                                                type='text'
                                                value={getPaymentOptionStatusLabel(
                                                  selectedPaymentOption.status
                                                )}
                                                disabled
                                                readOnly
                                                className='text-light/60'
                                              />
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        <div className={'inputWrapper'}>
                                          <div className={'label-light'}>Card number*</div>
                                          <div className={'no-border-input-field'}>
                                            <input
                                              type='text'
                                              value={`**** **** **** ${selectedPaymentOption?.cardEndingDigits || ''}`}
                                              disabled
                                              readOnly
                                              className='text-light/60'
                                            />
                                          </div>
                                        </div>
                                        <div className={'inputWrapper'}>
                                          <div className={'label-light'}>Cardholder name*</div>
                                          <div className={'no-border-input-field'}>
                                            <input
                                              type='text'
                                              value={selectedPaymentOption?.cardHolderName || ''}
                                              disabled
                                              readOnly
                                              className='text-light/60'
                                            />
                                          </div>
                                        </div>
                                        <div className={'inputWrapper'}>
                                          <div className={'label-light'}>Expiration date*</div>
                                          <div className={'no-border-input-field'}>
                                            <input
                                              type='text'
                                              value={`${selectedPaymentOption?.expiryMonth || ''}/${selectedPaymentOption?.expiryYear || ''}`}
                                              disabled
                                              readOnly
                                              className='text-light/60'
                                            />
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </details>
                              </div>
                            </>
                          ) : isPreAuthorizedDebit ? (
                            <>
                              <div className={'inputWrapper'}>
                                <div className={'label-light'}>Bank name*</div>
                                <div className={'no-border-input-field'}>
                                  <input
                                    type='text'
                                    value={selectedPaymentOption?.bankName || ''}
                                    disabled
                                    readOnly
                                    className='text-light/60'
                                  />
                                </div>
                              </div>
                              <div className={'inputWrapper'}>
                                <div className={'label-light'}>Account name*</div>
                                <div className={'no-border-input-field'}>
                                  <input
                                    type='text'
                                    value={selectedPaymentOption?.bankAccountName || ''}
                                    disabled
                                    readOnly
                                    className='text-light/60'
                                  />
                                </div>
                              </div>
                              <div className='flex flex-col sm:flex-row gap-5 w-full'>
                                <div className={'inputWrapper w-full'}>
                                  <div className={'label-light'}>Transit number*</div>
                                  <div className={'no-border-input-field'}>
                                    <input
                                      type='text'
                                      value={selectedPaymentOption?.bankTransitNumber || ''}
                                      disabled
                                      readOnly
                                      className='text-light/60'
                                    />
                                  </div>
                                </div>
                                <div className={'inputWrapper w-full'}>
                                  <div className={'label-light'}>Institution number*</div>
                                  <div className={'no-border-input-field'}>
                                    <input
                                      type='text'
                                      value={selectedPaymentOption?.bankInstitutionNumber || ''}
                                      disabled
                                      readOnly
                                      className='text-light/60'
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className={'inputWrapper'}>
                                <div className={'label-light'}>Account number*</div>
                                <div className={'no-border-input-field'}>
                                  <input
                                    type='text'
                                    value={
                                      selectedPaymentOption?.bankAccountNumber
                                        ? selectedPaymentOption.bankAccountNumber.length > 4
                                          ? `****${selectedPaymentOption.bankAccountNumber.slice(-4)}`
                                          : selectedPaymentOption.bankAccountNumber
                                        : ''
                                    }
                                    disabled
                                    readOnly
                                    className='text-light/60'
                                  />
                                </div>
                              </div>
                              {selectedPaymentOption?.status && (
                                <div className={'inputWrapper'}>
                                  <div className={'label-light'}>Status*</div>
                                  <div className={'no-border-input-field'}>
                                    <input
                                      type='text'
                                      value={selectedPaymentOption.status
                                        .replace(/([A-Z])/g, ' $1')
                                        .replace(/^./, (str) => str.toUpperCase())
                                        .trim()}
                                      disabled
                                      readOnly
                                      className='text-light/60'
                                    />
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              {selectedPaymentOption?.status &&
                                selectedPaymentOption.status !==
                                  TenantPaymentOptionStatusEnum.Active && (
                                  <div className='mb-2 rounded-[10px] border border-amber-200 bg-amber-50 p-4'>
                                    <p className='text-[13px] font-medium text-amber-800'>
                                      {getPaymentOptionStatusMessage(selectedPaymentOption.status)}
                                    </p>
                                    <p className='mt-1 text-xs text-amber-700'>
                                      Update this card or add a new payment method to use for
                                      payments.
                                    </p>
                                  </div>
                                )}
                              <div className={'inputWrapper'}>
                                <div className={'label-light'}>Card number*</div>
                                <div className={'no-border-input-field'}>
                                  <input
                                    type='text'
                                    value={`**** **** **** ${selectedPaymentOption?.cardEndingDigits || ''}`}
                                    disabled
                                    className='text-light/60'
                                  />
                                </div>
                              </div>
                              <div className={'inputWrapper'}>
                                <div className={'label-light'}>Cardholder name*</div>
                                <div className={'no-border-input-field'}>
                                  <input
                                    type='text'
                                    value={selectedPaymentOption?.cardHolderName || ''}
                                    disabled
                                    className='text-light/60'
                                  />
                                </div>
                              </div>
                              <div className='flex flex-col sm:flex-row gap-5 w-full'>
                                <div className={'inputWrapper w-full'}>
                                  <div className={'label-light'}>Expiration date*</div>
                                  <div className={'no-border-input-field'}>
                                    <input
                                      type='text'
                                      value={`${selectedPaymentOption?.expiryMonth || ''}/${
                                        selectedPaymentOption?.expiryYear || ''
                                      }`}
                                      className='text-light/60'
                                      disabled
                                    />
                                  </div>
                                </div>
                                <div className={'inputWrapper w-full'}>
                                  <div className={'label-light'}>CVV*</div>
                                  <div
                                    className={'no-border-input-field'}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') {
                                        event.preventDefault();
                                      }
                                    }}
                                  >
                                    <input
                                      type='text'
                                      value='***'
                                      className='text-light/60'
                                      disabled
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className={'inputWrapper'}>
                                <div className={'label flex justify-between w-full'}>
                                  <span>Billing address*</span>
                                  <div
                                    className='flex gap-1 justify-end items-center cursor-pointer'
                                    onClick={() => setIsManually(!isSaveManually)}
                                  >
                                    <Pencil className='text-primary h-4 w-4' />
                                    <span className='text-primary text-sm font-medium'>
                                      Enter manually
                                    </span>
                                  </div>
                                </div>
                                <div className={'inputField '}>
                                  <input
                                    type='text'
                                    ref={addressInputRef}
                                    {...formik.getFieldProps('address')}
                                    className='text-light/60'
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') {
                                        event.preventDefault();
                                      }
                                    }}
                                  />
                                </div>

                                {isSaveManually && (
                                  <div className='flex flex-col gap-6 sm:gap-8 mt-6 sm:mt-8'>
                                    <div className='flex flex-col sm:flex-row gap-4 sm:gap-5 w-full'>
                                      <div className={'inputWrapper w-full'}>
                                        <div className={'label-light'}>Unit number</div>
                                        <div className={'no-border-input-field'}>
                                          <input
                                            id='unitNumber'
                                            type='text'
                                            className='text-light/60'
                                            placeholder='--'
                                            {...formik.getFieldProps('unitNumber')}
                                            onKeyDown={(event) => {
                                              if (event.key === 'Enter') {
                                                event.preventDefault();
                                              }
                                            }}
                                          />
                                        </div>
                                        {formik.touched.unitNumber && formik.errors.unitNumber && (
                                          <div className='error'>{formik.errors.unitNumber}</div>
                                        )}
                                      </div>
                                      <div className={'inputWrapper w-full'}>
                                        <div className={'label-light'}>Street number</div>
                                        <div className={'no-border-input-field'}>
                                          <input
                                            id='house'
                                            type='text'
                                            className='text-light/60'
                                            placeholder='--'
                                            {...formik.getFieldProps('house')}
                                            onKeyDown={(event) => {
                                              if (event.key === 'Enter') {
                                                event.preventDefault();
                                              }
                                            }}
                                          />
                                        </div>
                                        {formik.touched.house && formik.errors.house && (
                                          <div className='error'>{formik.errors.house}</div>
                                        )}
                                      </div>
                                    </div>

                                    <div className='flex flex-col sm:flex-row gap-4 sm:gap-5 w-full'>
                                      <div className={'inputWrapper w-full'}>
                                        <div className={'label-light'}>Street</div>
                                        <div className={'no-border-input-field'}>
                                          <input
                                            id='street'
                                            type='text'
                                            placeholder='--'
                                            className='text-light/60'
                                            {...formik.getFieldProps('street')}
                                            onKeyDown={(event) => {
                                              if (event.key === 'Enter') {
                                                event.preventDefault();
                                              }
                                            }}
                                          />
                                        </div>
                                        {formik.touched.street && formik.errors.street && (
                                          <div className='error'>{formik.errors.street}</div>
                                        )}
                                      </div>
                                      <div className={'inputWrapper w-full'}>
                                        <div className={'label-light'}>City*</div>
                                        <div className={'no-border-input-field'}>
                                          <input
                                            id='city'
                                            type='text'
                                            placeholder='--'
                                            className='text-light/60'
                                            {...formik.getFieldProps('city')}
                                            onKeyDown={(event) => {
                                              if (event.key === 'Enter') {
                                                event.preventDefault();
                                              }
                                            }}
                                          />
                                        </div>
                                        {formik.touched.city && formik.errors.city && (
                                          <div className='error'>{formik.errors.city}</div>
                                        )}
                                      </div>
                                    </div>

                                    <div className='flex flex-col sm:flex-row gap-4 sm:gap-5 w-full'>
                                      <div className={'inputWrapper w-full'}>
                                        <div className={'label-light'}>Province</div>
                                        <div className={'no-border-input-field'}>
                                          <input
                                            id='province'
                                            type='text'
                                            placeholder='--'
                                            className='text-light/60'
                                            {...formik.getFieldProps('province')}
                                            onKeyDown={(event) => {
                                              if (event.key === 'Enter') {
                                                event.preventDefault();
                                              }
                                            }}
                                          />
                                        </div>
                                        {formik.touched.province && formik.errors.province && (
                                          <div className='error'>{formik.errors.province}</div>
                                        )}
                                      </div>
                                      <div className={'inputWrapper w-full'}>
                                        <div className={'label-light'}>Postal code*</div>
                                        <div className={'no-border-input-field'}>
                                          <input
                                            id='postal'
                                            type='text'
                                            placeholder='--'
                                            className='text-light/60'
                                            {...formik.getFieldProps('postal')}
                                            onKeyDown={(event) => {
                                              if (event.key === 'Enter') {
                                                event.preventDefault();
                                              }
                                            }}
                                          />
                                        </div>
                                        {formik.touched.postal && formik.errors.postal && (
                                          <div className='error'>{formik.errors.postal}</div>
                                        )}
                                      </div>
                                    </div>

                                    <div className='flex flex-col sm:flex-row gap-4 sm:gap-5 w-full'>
                                      <div className={'inputWrapper w-full'}>
                                        <div className={'label-light'}>Province code</div>
                                        <div className={'no-border-input-field'}>
                                          <input
                                            id='provinceCode'
                                            type='text'
                                            placeholder='--'
                                            className='text-light/60'
                                            {...formik.getFieldProps('provinceCode')}
                                            onKeyDown={(event) => {
                                              if (event.key === 'Enter') {
                                                event.preventDefault();
                                              }
                                            }}
                                          />
                                        </div>
                                        {formik.touched.provinceCode &&
                                          formik.errors.provinceCode && (
                                            <div className='error'>
                                              {formik.errors.provinceCode}
                                            </div>
                                          )}
                                      </div>
                                      <div className={'inputWrapper w-full'}>
                                        <div className={'label-light'}>Country*</div>
                                        <div className={'no-border-input-field'}>
                                          <input
                                            id='country'
                                            type='text'
                                            placeholder='Country'
                                            className='text-light/60'
                                            {...formik.getFieldProps('country')}
                                            disabled
                                          />
                                        </div>
                                        {formik.touched.country && formik.errors.country && (
                                          <div className='error'>{formik.errors.provinceCode}</div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </>
                          )}

                          {/* Default checkbox */}
                          <div className='flex items-center gap-2.5 pt-2 border-t border-[#E8EAF0]'>
                            <input
                              type='checkbox'
                              id='setDefault'
                              name='isDefault'
                              onChange={formik.handleChange}
                              checked={formik.values.isDefault}
                              className='h-5 w-5 text-primary border-gray-300 rounded-[4px] focus:text-primary'
                            />
                            <label
                              htmlFor='setDefault'
                              className='text-[13px] font-medium text-[#0A0B14] cursor-pointer'
                            >
                              Make default payment method
                            </label>
                          </div>

                          {/* Action buttons */}
                          <div className='flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 w-full'>
                            <button
                              className='w-full cancelBtn'
                              type='button'
                              onClick={() => {
                                setIsManually(false);
                              }}
                            >
                              Cancel
                            </button>
                            <button className='w-full submitBtn' type='submit'>
                              {isSaving && <ButtonSpinner />}
                              <span className='ml-2'>Save changes</span>
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </motion.section>
      )}
    </div>
  );
};

export default PaymentMethods;
